// @flow

import React, { useContext } from "react";
import { Field, useField, useForm } from "react-final-form";
import classnames from "classnames";
import { OnChange } from "react-final-form-listeners";

import type { FieldRenderProps } from "react-final-form";

import ShortFormSharedContext from "../../../misc/context";

import Cities from "./City";
import PostalCode from "./PostalCode";

import { WhenFieldChanges, Row, Label } from "../../shared/CommonComponents";
import { read } from "modules/ShortForm/misc/utilities";

import { isPostalCode } from "../../../misc/validations";
import { getLocations } from "Commons/Modules/Location/misc/Api";
import { mapLocations } from "Commons/Components/FormComponents/misc/Mappers";

import * as translations from "flavours/app-remind.me/i18n/validation.json";

const LocationsHandler = () => {
  const {
    configurationData: {
      translations: { fieldLabels },
    },
    category: { categoryType },
    inheritedStyles: { formRow, formRowPaired, formLabelLight },
  } = useContext(ShortFormSharedContext);
  const form = useForm();

  const locationIdField = useField("locationId");
  const locationField = useField("location");
  const locationMainNameField = useField("locationMainName");
  const locationPartNameField = useField("locationPartName");

  const fetchLocations = (postalcodeFieldValue, onChangeCallback) => {
    return getLocations({ type: categoryType }, postalcodeFieldValue);
  };

  const buildLocations = (data) => {
    return data?.map(mapLocations) || [];
  };

  const pushLocations = (dropdownLocations, onChangeCallback) => {
    onChangeCallback(dropdownLocations || null);
    preSelectUniqueLocation(dropdownLocations);
  };

  const resetLocations = (onChangeCallback) => {
    onChangeCallback(null);
  };

  const createLocations = async (postalCodeFieldValue, onChangeCallback) => {
    try {
      const data = await fetchLocations(postalCodeFieldValue);
      const dropdownLocations = buildLocations(data);
      pushLocations(dropdownLocations, onChangeCallback);
      invalidPostalCodeWarning("off");
    } catch (error) {
      console.error(error);
      invalidPostalCodeWarning("on");
    }
  };

  const preSelectUniqueLocation = (dropdownLocations) => {
    if (dropdownLocations.length === 1) {
      locationIdField.input.onChange(dropdownLocations[0].value);
    }
  };

  const invalidPostalCodeWarning = (status) => {
    if (status === "off") form.mutators.setFieldData("postalcode", { error: undefined });
    if (status === "on") form.mutators.setFieldData("postalcode", { error: translations.invalidPostalCode });
  };

  return (
    <Field name="locations">
      {({ input: { onChange, value }, meta }: FieldRenderProps) => {
        const locationsData = value;
        return (
          <>
            <Row className={classnames(formRow, formRowPaired)}>
              <Label className={formLabelLight}>{read(fieldLabels.location.postalcode)}</Label>
              <PostalCode />
            </Row>

            <Row className={classnames(formRow, formRowPaired)}>
              <Label className={formLabelLight}>{read(fieldLabels.location.city)}</Label>
              <Cities locations={locationsData || null} />
            </Row>

            <OnChange name={"postalcode"}>
              {(postalCodeFieldValue) => {
                if (!postalCodeFieldValue || isPostalCode(postalCodeFieldValue) !== undefined) {
                  resetLocations(onChange);
                } else {
                  isPostalCode(postalCodeFieldValue) === undefined
                    ? createLocations(postalCodeFieldValue, onChange)
                    : resetLocations(onChange);
                }
              }}
            </OnChange>
            <OnChange name={"locationId"}>
              {(locationId) => {
                const selectedCity = locationsData && locationsData.find((location) => location.value === locationId);
                locationField.input.onChange(selectedCity.text);
                locationMainNameField.input.onChange(selectedCity.mainname);
                locationPartNameField.input.onChange(selectedCity.partname);
              }}
            </OnChange>
            <WhenFieldChanges field="locations" becomes={null} set="locationId" to={null} />
          </>
        );
      }}
    </Field>
  );
};

export default LocationsHandler;
